import React, { useState } from 'react';
import Styles from '../BrandInsightsDashboard.module.scss';
import { FiLayers, FiInstagram, FiLinkedin, FiTwitter, FiYoutube, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { FaTiktok } from 'react-icons/fa';

const PLATFORM_ICONS = {
  instagram: FiInstagram,
  linkedin: FiLinkedin,
  twitter: FiTwitter,
  youtube: FiYoutube,
  tiktok: FaTiktok,
};

const PLATFORM_COLORS = {
  instagram: '#E4405F',
  linkedin: '#0A66C2',
  twitter: '#1DA1F2',
  youtube: '#FF0000',
  tiktok: '#000000',
};

const PLATFORM_NAMES = {
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  twitter: 'Twitter/X',
  youtube: 'YouTube',
  tiktok: 'TikTok',
};

const PlatformDeepDive = ({ platformDeepDives }) => {
  const [expandedPlatform, setExpandedPlatform] = useState(null);

  if (!platformDeepDives || Object.keys(platformDeepDives).length === 0) {
    return (
      <div className={Styles.platformSection}>
        <div className={Styles.sectionHeader}>
          <FiLayers className={Styles.sectionIcon} />
          <h2>Platform Intelligence</h2>
        </div>
        <div className={Styles.emptyCard}>
          <p>No platform data available. Connect your social channels to see insights.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={Styles.platformSection}>
      <div className={Styles.sectionHeader}>
        <FiLayers className={Styles.sectionIcon} />
        <h2>Platform Intelligence</h2>
      </div>

      <div className={Styles.platformGrid}>
        {Object.entries(platformDeepDives).map(([platform, data]) => {
          const Icon = PLATFORM_ICONS[platform] || FiLayers;
          const isExpanded = expandedPlatform === platform;
          const voice = data.voice_fingerprint || {};
          const pillars = data.content_pillars || [];
          const patterns = data.patterns || {};
          const hookPatterns = data.hook_patterns || {};

          return (
            <div
              key={platform}
              className={`${Styles.platformDeepCard} ${isExpanded ? Styles.expanded : ''}`}
            >
              {/* Platform Header */}
              <div
                className={Styles.platformDeepHeader}
                onClick={() => setExpandedPlatform(isExpanded ? null : platform)}
                style={{ borderLeftColor: PLATFORM_COLORS[platform] }}
              >
                <Icon
                  className={Styles.platformIcon}
                  style={{ color: PLATFORM_COLORS[platform] }}
                />
                <div className={Styles.platformHeaderInfo}>
                  <h4>{PLATFORM_NAMES[platform] || platform}</h4>
                  <span className={Styles.postsAnalyzed}>
                    {data.posts_analyzed || 0} posts analyzed
                  </span>
                </div>
                <div className={Styles.platformEngagement}>
                  <span className={Styles.engValue}>{data.avg_engagement?.toFixed(1) || '0'}%</span>
                  <span className={Styles.engLabel}>avg engagement</span>
                </div>
                <button className={Styles.expandBtn}>
                  {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                </button>
              </div>

              {/* Quick Stats (always visible) */}
              <div className={Styles.platformQuickStats}>
                {voice.tone && (
                  <div className={Styles.quickStat}>
                    <span className={Styles.quickStatLabel}>Voice:</span>
                    <span className={Styles.quickStatValue}>
                      {voice.tone}, {voice.writing_style}
                    </span>
                  </div>
                )}
                {pillars[0] && (
                  <div className={Styles.quickStat}>
                    <span className={Styles.quickStatLabel}>Top Pillar:</span>
                    <span className={Styles.quickStatValue}>
                      {pillars[0].name} ({pillars[0].percentage}%)
                    </span>
                  </div>
                )}
              </div>

              {/* Expanded Details */}
              {isExpanded && (
                <div className={Styles.platformExpandedContent}>
                  {/* Voice on this platform */}
                  <div className={Styles.platformDetailSection}>
                    <h5>Voice on {PLATFORM_NAMES[platform]}</h5>
                    {voice.personality_traits?.length > 0 && (
                      <div className={Styles.traitChipsSmall}>
                        {voice.personality_traits.slice(0, 4).map((trait, idx) => (
                          <span key={idx} className={Styles.traitChipSmall}>{trait}</span>
                        ))}
                      </div>
                    )}
                    {voice.signature_phrases?.length > 0 && (
                      <div className={Styles.phrasesSmall}>
                        <span className={Styles.phrasesLabel}>Signature phrases:</span>
                        <ul>
                          {voice.signature_phrases.slice(0, 3).map((phrase, idx) => (
                            <li key={idx}>"{phrase}"</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Content Pillars */}
                  {pillars.length > 0 && (
                    <div className={Styles.platformDetailSection}>
                      <h5>Content Breakdown</h5>
                      <div className={Styles.miniPillars}>
                        {pillars.slice(0, 4).map((pillar, idx) => (
                          <div key={idx} className={Styles.miniPillarRow}>
                            <span className={Styles.miniPillarName}>{pillar.name}</span>
                            <div className={Styles.miniPillarBar}>
                              <div
                                className={Styles.miniPillarFill}
                                style={{
                                  width: `${pillar.percentage}%`,
                                  backgroundColor: PLATFORM_COLORS[platform]
                                }}
                              />
                            </div>
                            <span className={Styles.miniPillarPercent}>{pillar.percentage}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Hook Patterns */}
                  {hookPatterns.distribution && Object.keys(hookPatterns.distribution).length > 0 && (
                    <div className={Styles.platformDetailSection}>
                      <h5>Hook Patterns</h5>
                      <div className={Styles.hookStats}>
                        {Object.entries(hookPatterns.distribution)
                          .sort((a, b) => b[1] - a[1])
                          .slice(0, 3)
                          .map(([hook, pct], idx) => (
                            <span key={idx} className={Styles.hookStat}>
                              {formatHookType(hook)}: {pct}%
                            </span>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Best Posting Times */}
                  {patterns.best_posting_times && (
                    <div className={Styles.platformDetailSection}>
                      <h5>Best Posting Times</h5>
                      <div className={Styles.postingTimes}>
                        {patterns.best_posting_times.best_day && (
                          <span className={Styles.postingTime}>
                            <strong>{patterns.best_posting_times.best_day}s</strong> perform best
                          </span>
                        )}
                        {patterns.best_posting_times.best_hour !== undefined && (
                          <span className={Styles.postingTime}>
                            Around <strong>{formatHour(patterns.best_posting_times.best_hour)}</strong>
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Hashtag Performance */}
                  {patterns.hashtag_analysis?.top_performing_hashtags?.length > 0 && (
                    <div className={Styles.platformDetailSection}>
                      <h5>Top Hashtags</h5>
                      <div className={Styles.hashtagList}>
                        {patterns.hashtag_analysis.top_performing_hashtags.slice(0, 5).map((ht, idx) => (
                          <span key={idx} className={Styles.hashtagItem}>
                            #{ht.tag} <span className={Styles.hashtagEng}>{ht.avg_engagement?.toFixed(1)}%</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Content Length */}
                  {patterns.content_length_analysis?.optimal_length && (
                    <div className={Styles.platformDetailSection}>
                      <h5>Optimal Content Length</h5>
                      <span className={Styles.lengthNote}>
                        {patterns.content_length_analysis.optimal_length} posts perform best
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Helpers
const formatHookType = (hookType) => {
  const formatMap = {
    'question': 'Questions',
    'number': 'Numbers',
    'story': 'Stories',
    'bold_claim': 'Bold claims',
    'curiosity_gap': 'Curiosity',
    'contrarian': 'Contrarian',
    'personal': 'Personal'
  };
  return formatMap[hookType] || hookType?.replace(/_/g, ' ') || 'Unknown';
};

const formatHour = (hour) => {
  if (hour === 0) return '12am';
  if (hour === 12) return '12pm';
  if (hour < 12) return `${hour}am`;
  return `${hour - 12}pm`;
};

export default PlatformDeepDive;
